import type { ReactNode } from "react";
import type { Issue, ModuleResult } from "@ftr/tax-core";
import { useOrganizer } from "./context.tsx";

export const STATE_LABEL: Record<ModuleResult<unknown>["state"], string> = {
  ready: "Ready",
  provisional: "Needs a look",
  blocked: "Needs info",
  not_applicable: "Doesn't apply",
};

export function StateBadge({ state }: { state: ModuleResult<unknown>["state"] }) {
  return <span className={`badge badge--${state}`}>{STATE_LABEL[state]}</span>;
}

/** Issues that mention any of these ids, shown next to the record they're about. */
export function IssuesFor({ ids, exclude = [] }: { ids: string[]; exclude?: string[] }) {
  const { analysis } = useOrganizer();
  const list = analysis.issues.filter((i) => i.severity !== "info" && !exclude.includes(i.code) && i.refs.some((r) => ids.includes(r)));
  if (!list.length) return null;
  return (
    <ul className="issues issues--inline">
      {list.map((i) => (
        <IssueItem key={i.code + i.refs.join()} issue={i} />
      ))}
    </ul>
  );
}

export function IssueItem({ issue, children }: { issue: Issue; children?: ReactNode }) {
  return (
    <li className={`issue issue--${issue.severity}`}>
      <p className="issue-msg">{issue.message}</p>
      <p className="issue-next">{issue.nextAction}</p>
      {children}
    </li>
  );
}

export function Editor(props: { title: string; children: ReactNode; onSave: () => void; onCancel: () => void; saveLabel?: string; canSave?: boolean; error?: string | null }) {
  return (
    <div className="editor" role="group" aria-label={props.title}>
      <p className="editor-title">{props.title}</p>
      {props.children}
      {props.error && <p className="field-msg">{props.error}</p>}
      <div className="editor-actions">
        <button type="button" className="btn btn--primary btn--sm" onClick={props.onSave} disabled={props.canSave === false}>
          {props.saveLabel ?? "Save"}
        </button>
        <button type="button" className="btn btn--plain btn--sm" onClick={props.onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

export function Row({ children, actions }: { children: ReactNode; actions?: ReactNode }) {
  return (
    <div className="row">
      <div className="row-main">{children}</div>
      {actions && <div className="row-actions">{actions}</div>}
    </div>
  );
}

export function personName(yf: { people: { id: string; label: string; role: string }[] }, id: string | "joint"): string {
  if (id === "joint") return "Joint";
  const p = yf.people.find((x) => x.id === id);
  return p ? (p.role === "primary" ? (p.label === "You" ? "You" : p.label) : p.label) : "Unknown";
}
